import React, { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  CheckCircle2, 
  Loader2, 
  AlertCircle, 
  Zap, 
  Trophy,
  Sparkles,
  ArrowRight,
  Mail,
  Shield,
  Clock,
  Globe,
  ArrowLeft,
  CreditCard
} from "lucide-react";

// Use a relative URL when in production, allowing the Nginx proxy to handle routing
const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const creditPackages = [
  {
    id: "basic",
    name: "Starter",
    credits: 50,
    price: "£2.50",
    pricePerCredit: "£0.05",
    packId: "50",
    description: "Perfect for trying out our service",
    features: ["50 video summaries", "All platforms access", "Email support"],
    popular: false,
    icon: Zap,
  },
  {
    id: "standard",
    name: "Popular",
    credits: 100,
    price: "£5",
    pricePerCredit: "£0.05",
    packId: "100",
    description: "Best for regular users",
    features: ["100 video summaries", "All platforms access", "Priority support"],
    popular: true,
    icon: Sparkles,
  },
  {
    id: "premium",
    name: "Pro",
    credits: 250,
    price: "£12.50",
    pricePerCredit: "£0.05",
    packId: "250",
    description: "Maximum value for power users",
    features: ["250 video summaries", "All platforms access", "Priority support", "Best value"],
    popular: false,
    icon: Trophy,
  },
];

const BuyCredits = ({ apiKey }) => {
  const [email, setEmail] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Get email from localStorage if available
  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setError(null);
    // If user is logged in, go directly to checkout
    if (apiKey) {
      handleSubmit();
    } else {
      setShowEmailForm(true);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!selectedPackage) {
      setError("Please select a credit package");
      return;
    }

    // Use a default email if user is logged in but no email is provided
    let checkoutEmail = email;
    if (!checkoutEmail && apiKey) {
      checkoutEmail = localStorage.getItem("userEmail") || "user@example.com";
      // Save the email for future use
      if (!localStorage.getItem("userEmail")) {
        localStorage.setItem("userEmail", checkoutEmail);
      }
    }

    if (!checkoutEmail && !apiKey) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/checkout`, {
        email: checkoutEmail,
        pack_id: selectedPackage.packId,
        api_key: apiKey || "",
      });

      // Open Stripe checkout in a new window
      const checkoutWindow = window.open(response.data.url, "_blank");

      // Fallback in case popup blockers prevent the window from opening
      if (
        !checkoutWindow ||
        checkoutWindow.closed ||
        typeof checkoutWindow.closed === "undefined"
      ) {
        // Redirect in the same window if popup was blocked
        window.location.href = response.data.url;
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(
          `Checkout failed: ${err.response.data.error || "Unknown error"}`
        );
      } else {
        setError("Failed to create checkout session. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPricing = () => {
    setShowEmailForm(false);
    setSelectedPackage(null);
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>
          {apiKey ? "Buy More Credits" : "Get Started"} | RecapMate
        </title>
        <meta
          name="description"
          content="Purchase credits for RecapMate to summarize YouTube videos and websites. Choose from various packages to suit your needs."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-subtle">
        {/* Hero Section */}
        <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium animate-in fade-in slide-in duration-500">
              <Zap className="w-4 h-4 mr-2" />
              Simple, Transparent Pricing
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight animate-in fade-in slide-in duration-700">
              {apiKey ? "Top Up Your Credits" : "Choose Your Plan"}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto animate-in fade-in slide-in duration-900">
              Each credit lets you summarize one YouTube video or website of any length. 
              No subscriptions, no hidden fees.
            </p>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {!showEmailForm ? (
              <>
                {/* Pricing Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  {creditPackages.map((pkg) => {
                    const Icon = pkg.icon;
                    
                    return (
                      <Card
                        key={pkg.id}
                        className={`relative cursor-pointer transition-all duration-300 hover-lift hover:shadow-lg flex flex-col h-full ${
                          pkg.popular ? "ring-2 ring-primary/20 shadow-lg" : ""
                        }`}
                        onClick={() => handlePackageSelect(pkg)}
                      >
                        <CardHeader className="text-center pb-4">
                          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                            pkg.popular ? "bg-gradient-primary" : "bg-muted"
                          }`}>
                            <Icon className={`h-8 w-8 ${pkg.popular ? "text-white" : "text-muted-foreground"}`} />
                          </div>
                          <div className="space-y-2">
                            <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                            {pkg.popular && (
                              <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                                MOST POPULAR
                              </span>
                            )}
                          </div>
                          <p className="text-muted-foreground mt-2">{pkg.description}</p>
                        </CardHeader>
                        
                        <CardContent className="flex flex-col flex-grow space-y-6">
                          <div className="text-center">
                            <div className="text-4xl font-bold">{pkg.price}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {pkg.pricePerCredit} per credit
                            </div>
                          </div>
                          
                          <div className="bg-muted/50 rounded-lg p-4 text-center">
                            <span className="text-3xl font-bold gradient-text">{pkg.credits}</span>
                            <span className="text-muted-foreground ml-2">credits</span>
                          </div>
                          
                          <ul className="space-y-3 flex-grow">
                            {pkg.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                <span className="text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <Button
                            className={`w-full mt-auto ${
                              pkg.popular
                                ? "gradient-primary text-white border-0"
                                : ""
                            }`}
                            variant={pkg.popular ? "default" : "outline"}
                          >
                            Select Plan
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Features Section */}
                <Card className="max-w-4xl mx-auto shadow-lg">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl">Everything Included</CardTitle>
                    <p className="text-muted-foreground mt-2">
                      All plans come with full access to RecapMate's features
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Sparkles className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">AI-Powered Summaries</h3>
                          <p className="text-sm text-muted-foreground">
                            Advanced Gemini AI generates accurate, comprehensive summaries
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Clock className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">Any Length Videos</h3>
                          <p className="text-sm text-muted-foreground">
                            Works with videos and websites of any length or complexity
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Globe className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">Multi-Platform Access</h3>
                          <p className="text-sm text-muted-foreground">
                            Use on web, Telegram bot, and Chrome extension
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Shield className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">Secure & Private</h3>
                          <p className="text-sm text-muted-foreground">
                            Your data is secure and we never store video content
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Email Collection Step */
              <div className="max-w-lg mx-auto animate-in fade-in slide-in duration-500">
                {/* Selected Package Summary */}
                <Card className="mb-8 shadow-lg">
                  <CardHeader className="pb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleBackToPricing}
                      className="w-fit mb-4"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to pricing
                    </Button>
                    <CardTitle className="text-xl">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {selectedPackage && (
                          <>
                            <selectedPackage.icon className="h-8 w-8 text-primary" />
                            <div>
                              <p className="font-semibold">{selectedPackage.name} Plan</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedPackage.credits} credits
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{selectedPackage?.price}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Email Form */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Enter Your Email
                    </CardTitle>
                    <p className="text-muted-foreground mt-2">
                      We'll send your API key to this email address
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <Input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full h-12 text-base focus-ring"
                          required
                          autoFocus
                        />
                      </div>

                      {error && (
                        <Alert variant="destructive" className="animate-in fade-in slide-in">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full gradient-primary text-white border-0 hover:opacity-90 transition-all hover-lift"
                        disabled={loading || !email}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            Continue to Payment
                          </>
                        )}
                      </Button>

                      <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Secure payment via Stripe
                        </p>
                        <div className="flex items-center justify-center gap-4 text-muted-foreground">
                          <Shield className="h-4 w-4" />
                          <span className="text-xs">SSL Encrypted</span>
                        </div>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Support Section */}
            <div className="text-center mt-12">
              <p className="text-muted-foreground">
                Have questions? {" "}
                <a
                  href="mailto:support@recapmate.com"
                  className="text-primary hover:underline font-medium"
                >
                  Contact our support team
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BuyCredits;